<script setup lang="ts">
import { computed, type Component } from 'vue'
import { NModal } from 'naive-ui'
import { X, AlertCircle, CheckCircle2, XCircle, Info } from 'lucide-vue-next'
import type { ModalVariant } from '@/shared/types/modal.types'
import { STATUS_COLORS } from '@/shared/constants/colors.constants'

export type { ModalVariant }

const props = withDefaults(
  defineProps<{
    show: boolean
    title?: string
    description?: string
    variant?: ModalVariant
    width?: string
    closable?: boolean
    maskClosable?: boolean
    scrollable?: boolean
  }>(),
  {
    title: undefined,
    description: undefined,
    variant: 'default',
    width: '500px',
    closable: true,
    maskClosable: true,
    scrollable: false,
  }
)

const emit = defineEmits<{
  'update:show': [value: boolean]
  close: []
}>()

function handleClose() {
  emit('update:show', false)
  emit('close')
}

const VARIANT_ICON_MAP: Record<ModalVariant, Component | null> = {
  default: null,
  success: CheckCircle2,
  warning: AlertCircle,
  error: XCircle,
  info: Info,
}

const VARIANT_COLOR_MAP: Record<ModalVariant, string> = {
  default: '',
  success: STATUS_COLORS.success,
  warning: STATUS_COLORS.warning,
  error: STATUS_COLORS.error,
  info: STATUS_COLORS.info,
}

const variantIcon = computed(() => VARIANT_ICON_MAP[props.variant])
const variantColor = computed(() => VARIANT_COLOR_MAP[props.variant])
</script>

<template>
  <NModal
    :show="show"
    :mask-closable="maskClosable"
    transform-origin="center"
    @update:show="
      (val: boolean) => {
        if (!val) handleClose()
      }
    "
  >
    <div
      class="app-modal"
      :class="{ 'app-modal--status': variant !== 'default' }"
      :style="{ width, maxWidth: '95vw' }"
    >
      <button v-if="closable" class="app-modal__close" @click="handleClose">
        <X :size="18" :stroke-width="1.5" class="text-ink-muted" />
      </button>

      <div v-if="variantIcon && !$slots.icon" class="app-modal__icon">
        <component :is="variantIcon" :size="40" :stroke-width="2" :color="variantColor" />
      </div>

      <div v-if="$slots.icon" class="app-modal__icon">
        <slot name="icon"></slot>
      </div>

      <div v-if="title || $slots.header" class="app-modal__header">
        <slot name="header">
          <h2 class="app-modal__title">{{ title }}</h2>
        </slot>
        <p v-if="description" class="app-modal__description">{{ description }}</p>
      </div>

      <div
        v-if="$slots.default"
        class="app-modal__content"
        :class="{ 'app-modal__content--scrollable': scrollable }"
      >
        <slot></slot>
      </div>

      <div v-if="$slots.footer" class="app-modal__footer">
        <slot name="footer"></slot>
      </div>
    </div>
  </NModal>
</template>
